import type {
  CreateTextRequest,
  CreateTextResponse,
  GetTextsResponse,
} from '@compass/client/types/text';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { textService } from '@/services/api';
import { useStore } from '@/store/store';

/**
 * Hook to fetch all text messages for a case record
 */
export const useTexts = (
  caseRecordId: string | null,
  options?: { refetchInterval?: number | false; limit?: number },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const isEnabled = !!caseRecordId && !!selectedOrganizationId && !!selectedProgramId;

  return useQuery<GetTextsResponse>({
    queryKey: ['texts', caseRecordId, testModeEnabled, options?.limit],
    queryFn: async () => {
      // Return empty response if in test mode
      if (testModeEnabled) {
        return { texts: [] };
      }

      return textService.getTexts({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseRecordId!,
        limit: options?.limit,
      });
    },
    enabled: isEnabled && !testModeEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: options?.refetchInterval,
  });
};

/**
 * Hook to create a new text message
 */
export const useCreateText = () => {
  const queryClient = useQueryClient();
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation<CreateTextResponse, Error, { caseRecordId: string; data: CreateTextRequest }>({
    mutationFn: async ({ caseRecordId, data }) => {
      if (testModeEnabled) {
        throw new Error('Text messages are not supported in test mode');
      }
      return textService.createText({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_record_id: caseRecordId,
        ...data,
      });
    },
    onSuccess: (_data, variables) => {
      // Invalidate text messages list to include new text
      queryClient.invalidateQueries({
        queryKey: ['texts', variables.caseRecordId],
      });
    },
  });
};
